import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UploadSimple, FileText, ArrowRight, MapPin, Briefcase, Lightning, CurrencyDollar, Plus, Trash } from "@phosphor-icons/react";
import Header from "@/components/app/Header";

export default function Onboarding() {
  const nav = useNavigate();
  const { profile, setProfile } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");

  const [manual, setManual] = useState({
    full_name: "",
    headline: "",
    summary: "",
    skills: "",
    experience_years: 0,
    preferred_roles: "",
    preferred_locations: "",
    seniority: "mid",
    work_type_preference: "any",
    salary_min: "",
    salary_max: "",
    excluded_keywords: "",
    experience: [],
    projects: []
  });

  useEffect(() => {
    if (profile) {
      setManual({
        full_name: profile.full_name || "",
        headline: profile.headline || "",
        summary: profile.summary || "",
        skills: (profile.skills || []).join(", "),
        experience_years: profile.experience_years || 0,
        preferred_roles: (profile.preferred_roles || []).join(", "),
        preferred_locations: (profile.preferred_locations || []).join(", "),
        seniority: profile.seniority || "mid",
        work_type_preference: profile.work_type_preference || "any",
        salary_min: profile.salary_min || "",
        salary_max: profile.salary_max || "",
        excluded_keywords: (profile.excluded_keywords || []).join(", "),
        experience: profile.experience || [],
        projects: profile.projects || [],
      });
    }
  }, [profile]);

  const [savingManual, setSavingManual] = useState(false);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/profile/resume", form, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(data);
      toast.success("Resume parsed — profile ready");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to parse resume");
    } finally {
      setUploading(false);
    }
  };

  const saveManual = async (e) => {
    e.preventDefault();
    if (!manual.full_name.trim()) {
      toast.error("Please provide your full name");
      return;
    }

    setSavingManual(true);
    try {
      const payload = {
        full_name: manual.full_name,
        headline: manual.headline,
        summary: manual.summary,
        skills: manual.skills.split(",").map((s) => s.trim()).filter(Boolean),
        experience_years: Number(manual.experience_years) || 0,
        preferred_roles: manual.preferred_roles.split(",").map((s) => s.trim()).filter(Boolean),
        preferred_locations: manual.preferred_locations.split(",").map((s) => s.trim()).filter(Boolean),
        seniority: manual.seniority,
        work_type_preference: manual.work_type_preference,
        salary_min: manual.salary_min ? parseInt(manual.salary_min) : null,
        salary_max: manual.salary_max ? parseInt(manual.salary_max) : null,
        excluded_keywords: manual.excluded_keywords.split(",").map(k => k.trim()).filter(Boolean),
        experience: manual.experience,
        projects: manual.projects,
      };
      const { data } = await api.post("/profile/manual", payload);
      setProfile(data);
      toast.success("Profile saved");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingManual(false);
    }
  };

  const addExperience = () => {
    setManual({ ...manual, experience: [...manual.experience, { role: "", company: "", duration: "", highlights: [] }] });
  };
  
  const updateExperience = (index, field, value) => {
    const newExp = [...manual.experience];
    newExp[index][field] = value;
    setManual({ ...manual, experience: newExp });
  };

  const removeExperience = (index) => {
    const newExp = [...manual.experience];
    newExp.splice(index, 1);
    setManual({ ...manual, experience: newExp });
  };

  const addProject = () => {
    setManual({ ...manual, projects: [...manual.projects, { name: "", description: "", role: "", url: "" }] });
  };

  const updateProject = (index, field, value) => {
    const newProj = [...manual.projects];
    newProj[index][field] = value;
    setManual({ ...manual, projects: newProj });
  };

  const removeProject = (index) => {
    const newProj = [...manual.projects];
    newProj.splice(index, 1);
    setManual({ ...manual, projects: newProj });
  };

  const isEditMode = !!profile;

  return (
    <div className="min-h-screen bg-slate-50/50 pb-20">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="mb-10">
          <span className="tick-label">{isEditMode ? "Profile · Settings" : "Step 01 · Build your scope"}</span>
          <h1 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter">
            {isEditMode ? "Update your profile." : "Tell Opplio who you are."}
          </h1>
          <p className="mt-4 text-ink-soft max-w-2xl">
            {isEditMode 
              ? "Modify your experience, projects, and target roles to improve AI matches and draft quality." 
              : "Upload a resume for instant parsing, or fill in the quick form. You can refine everything later."}
          </p>
        </div>

        <Tabs defaultValue={isEditMode ? "manual" : "resume"} className="w-full">
          <TabsList className="rounded-xl bg-white border border-slate-200 p-0 h-12 mb-6 inline-flex">
            {!isEditMode && (
              <TabsTrigger
                value="resume"
                className="rounded-xl h-full px-6 text-xs uppercase tracking-wider font-semibold data-[state=active]:bg-ink data-[state=active]:text-white"
              >
                <UploadSimple size={14} weight="bold" className="mr-2" />Upload resume
              </TabsTrigger>
            )}
            <TabsTrigger
              value="manual"
              className="rounded-xl h-full px-6 text-xs uppercase tracking-wider font-semibold data-[state=active]:bg-ink data-[state=active]:text-white"
            >
              <FileText size={14} weight="bold" className="mr-2" />{isEditMode ? "Edit Manually" : "Fill manually"}
            </TabsTrigger>
          </TabsList>

          {!isEditMode && (
            <TabsContent value="resume" className="mt-0">
              <div className="border border-slate-200 bg-white">
                <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
                  <span className="tick-label">Upload · PDF or DOCX · max 5MB</span>
                  <span className="font-mono text-[11px] text-ink-soft">AI PARSER</span>
                </div>
                <label
                  htmlFor="resume-file"
                  className={`block cursor-pointer p-10 lg:p-16 text-center transition-all duration-300 rounded-xl border-2 border-dashed m-6 ${
                    uploading 
                      ? "border-brand bg-brand/5 shadow-[0_0_30px_rgba(0,47,167,0.15)] animate-pulse" 
                      : "border-slate-200 hover:border-brand/50 hover:bg-slate-50/40"
                  }`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files?.[0] && !uploading) {
                      onFileChange({ target: { files: e.dataTransfer.files } });
                    }
                  }}
                >
                  <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all duration-500 ${uploading ? "bg-brand/10 shadow-[0_0_20px_rgba(0,47,167,0.2)]" : "bg-slate-50"}`}>
                    <UploadSimple size={32} weight="duotone" className={uploading ? "text-brand" : "text-ink-mute"} />
                  </div>
                  <h3 className="font-display font-bold text-2xl tracking-tight">
                    {uploading ? "Analyzing…" : fileName || "Drop your resume"}
                  </h3>
                  <p className="mt-2 text-sm text-ink-soft">
                    {uploading ? "Extracting your skills and experience." : "Drag & drop a PDF or DOCX, or click to browse."}
                  </p>
                  <Button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className={`mt-6 rounded-xl text-white uppercase tracking-wider text-xs h-11 px-6 transition-all duration-300 ${uploading ? "bg-brand/50" : "bg-brand hover:bg-brand-hover"}`}
                  >
                    {uploading ? "Working…" : "Choose file"} {!uploading && <ArrowRight size={14} weight="bold" className="ml-2" />}
                  </Button>
                  <input
                    id="resume-file"
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={onFileChange}
                  />
                </label>
              </div>
            </TabsContent>
          )}

          <TabsContent value="manual" className="mt-0">
            <form onSubmit={saveManual} className="space-y-8">
              
              {/* Basic Info */}
              <div className="border border-slate-200 bg-white p-6 lg:p-8">
                <h2 className="font-display font-bold text-xl mb-6 flex items-center gap-2"><FileText size={20} className="text-brand"/> Basic Info</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <Label htmlFor="full_name" className="tick-label">Full name <span className="text-red-500">*</span></Label>
                    <Input id="full_name" value={manual.full_name} onChange={(e) => setManual({ ...manual, full_name: e.target.value })} required className="rounded-xl mt-2 h-11" placeholder="Ada Lovelace" />
                  </div>
                  <div>
                    <Label htmlFor="headline" className="tick-label">Headline</Label>
                    <Input id="headline" placeholder="Senior Frontend Engineer" value={manual.headline} onChange={(e) => setManual({ ...manual, headline: e.target.value })} className="rounded-xl mt-2 h-11" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="summary" className="tick-label">Summary · 2-3 sentences</Label>
                  <Textarea id="summary" rows={4} value={manual.summary} onChange={(e) => setManual({ ...manual, summary: e.target.value })} className="rounded-xl mt-2" placeholder="Write a short summary about your professional background..." />
                </div>
              </div>

              {/* Skills & Preferences */}
              <div className="border border-slate-200 bg-white p-6 lg:p-8">
                <h2 className="font-display font-bold text-xl mb-6 flex items-center gap-2"><Lightning size={20} className="text-brand"/> Skills & Preferences</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <Label htmlFor="skills" className="tick-label">Skills · comma separated</Label>
                    <Textarea id="skills" rows={3} placeholder="React, TypeScript, PostgreSQL" value={manual.skills} onChange={(e) => setManual({ ...manual, skills: e.target.value })} className="rounded-xl mt-2" />
                  </div>
                  <div>
                    <Label htmlFor="preferred_roles" className="tick-label">Target roles · comma separated</Label>
                    <Textarea id="preferred_roles" rows={3} placeholder="Senior Frontend, Product Engineer" value={manual.preferred_roles} onChange={(e) => setManual({ ...manual, preferred_roles: e.target.value })} className="rounded-xl mt-2" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div>
                    <Label htmlFor="experience_years" className="tick-label">Years of experience</Label>
                    <div className="relative mt-2">
                      <Lightning size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                      <Input id="experience_years" type="number" min={0} max={50} value={manual.experience_years} onChange={(e) => setManual({ ...manual, experience_years: e.target.value })} className="rounded-xl pl-9 h-11" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="seniority" className="tick-label">Seniority</Label>
                    <Select value={manual.seniority || "mid"} onValueChange={(val) => setManual({ ...manual, seniority: val })}>
                      <SelectTrigger className="mt-2 h-11 w-full rounded-xl border border-slate-200/60 bg-white px-3 text-sm">
                        <SelectValue placeholder="Select seniority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="junior">Junior</SelectItem>
                        <SelectItem value="mid">Mid</SelectItem>
                        <SelectItem value="senior">Senior</SelectItem>
                        <SelectItem value="lead">Lead</SelectItem>
                        <SelectItem value="principal">Principal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="preferred_locations" className="tick-label">Preferred locations</Label>
                    <div className="relative mt-2">
                      <MapPin size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                      <Input id="preferred_locations" placeholder="Remote, Europe" value={manual.preferred_locations} onChange={(e) => setManual({ ...manual, preferred_locations: e.target.value })} className="rounded-xl pl-9 h-11" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <Label htmlFor="work_type" className="tick-label">Work Type</Label>
                    <Select value={manual.work_type_preference || "any"} onValueChange={(val) => setManual({ ...manual, work_type_preference: val })}>
                      <SelectTrigger className="mt-2 h-11 w-full rounded-xl border border-slate-200/60 bg-white px-3 text-sm">
                        <SelectValue placeholder="Select work type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any (Remote/Onsite)</SelectItem>
                        <SelectItem value="remote">Remote Only</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                        <SelectItem value="onsite">Onsite Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="salary_min" className="tick-label">Min Salary (USD)</Label>
                    <div className="relative mt-2">
                      <CurrencyDollar size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
                      <Input id="salary_min" type="number" placeholder="80000" value={manual.salary_min} onChange={(e) => setManual({ ...manual, salary_min: e.target.value })} className="rounded-xl pl-9 h-11" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="excluded_keywords" className="tick-label">Exclude keywords</Label>
                    <Input id="excluded_keywords" placeholder="web3, crypto, unpaid" value={manual.excluded_keywords} onChange={(e) => setManual({ ...manual, excluded_keywords: e.target.value })} className="rounded-xl mt-2 h-11" />
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div className="border border-slate-200 bg-white p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display font-bold text-xl flex items-center gap-2"><Briefcase size={20} className="text-brand"/> Experience</h2>
                  <Button type="button" onClick={addExperience} variant="outline" size="sm" className="rounded-xl border-brand text-brand hover:bg-brand/5 uppercase tracking-wider text-[10px] h-8">
                    <Plus size={12} weight="bold" className="mr-1.5" /> Add Role
                  </Button>
                </div>
                
                {manual.experience.length === 0 && (
                  <p className="text-sm text-ink-mute italic">No experience added. Click "Add Role" to add your work history.</p>
                )}

                <div className="space-y-6">
                  {manual.experience.map((exp, idx) => (
                    <div key={idx} className="relative border border-slate-200/60 bg-slate-50/30 p-5 rounded-xl">
                      <button type="button" onClick={() => removeExperience(idx)} className="absolute top-4 right-4 text-ink-mute hover:text-red-500 transition-colors">
                        <Trash size={18} />
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pr-8 mb-4">
                        <div>
                          <Label className="tick-label">Job Title</Label>
                          <Input value={exp.role} onChange={e => updateExperience(idx, "role", e.target.value)} className="mt-2 h-9 rounded-lg" placeholder="e.g. Software Engineer" />
                        </div>
                        <div>
                          <Label className="tick-label">Company Name</Label>
                          <Input value={exp.company} onChange={e => updateExperience(idx, "company", e.target.value)} className="mt-2 h-9 rounded-lg" placeholder="e.g. Google" />
                        </div>
                        <div>
                          <Label className="tick-label">Duration</Label>
                          <Input value={exp.duration} onChange={e => updateExperience(idx, "duration", e.target.value)} className="mt-2 h-9 rounded-lg" placeholder="e.g. Jan 2020 - Present" />
                        </div>
                      </div>
                      <div>
                        <Label className="tick-label">Highlights / Description</Label>
                        <Textarea 
                          value={exp.highlights.join("\n")} 
                          onChange={e => updateExperience(idx, "highlights", e.target.value.split("\n").filter(Boolean))} 
                          className="mt-2 rounded-lg" 
                          rows={3} 
                          placeholder="Shipped X using Y resulting in Z... (One per line)" 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Projects */}
              <div className="border border-slate-200 bg-white p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display font-bold text-xl flex items-center gap-2"><Lightning size={20} className="text-brand"/> Projects</h2>
                  <Button type="button" onClick={addProject} variant="outline" size="sm" className="rounded-xl border-brand text-brand hover:bg-brand/5 uppercase tracking-wider text-[10px] h-8">
                    <Plus size={12} weight="bold" className="mr-1.5" /> Add Project
                  </Button>
                </div>
                
                {manual.projects.length === 0 && (
                  <p className="text-sm text-ink-mute italic">No projects added. Click "Add Project" to showcase your work.</p>
                )}

                <div className="space-y-6">
                  {manual.projects.map((proj, idx) => (
                    <div key={idx} className="relative border border-slate-200/60 bg-slate-50/30 p-5 rounded-xl">
                      <button type="button" onClick={() => removeProject(idx)} className="absolute top-4 right-4 text-ink-mute hover:text-red-500 transition-colors">
                        <Trash size={18} />
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pr-8 mb-4">
                        <div>
                          <Label className="tick-label">Project Title</Label>
                          <Input value={proj.name} onChange={e => updateProject(idx, "name", e.target.value)} className="mt-2 h-9 rounded-lg" placeholder="e.g. AutoJobFinder" />
                        </div>
                        <div>
                          <Label className="tick-label">Your Role</Label>
                          <Input value={proj.role} onChange={e => updateProject(idx, "role", e.target.value)} className="mt-2 h-9 rounded-lg" placeholder="e.g. Lead Dev" />
                        </div>
                        <div>
                          <Label className="tick-label">Link (Optional)</Label>
                          <Input value={proj.url} onChange={e => updateProject(idx, "url", e.target.value)} className="mt-2 h-9 rounded-lg" placeholder="e.g. https://github.com/..." />
                        </div>
                      </div>
                      <div>
                        <Label className="tick-label">Description</Label>
                        <Textarea 
                          value={proj.description} 
                          onChange={e => updateProject(idx, "description", e.target.value)} 
                          className="mt-2 rounded-lg" 
                          rows={2} 
                          placeholder="Briefly describe what the project is and the tech used." 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Footer */}
              <div className="sticky bottom-4 z-10 flex items-center justify-between border border-slate-200 bg-white/80 backdrop-blur-md shadow-lg p-4 rounded-2xl">
                <div className="text-xs text-ink-soft inline-flex items-center gap-2 font-medium px-2">
                  <Briefcase size={16} weight="duotone" className="text-brand"/> Your data is stored against your account only.
                </div>
                <Button type="submit" disabled={savingManual} className="rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-11 px-8 shadow-md">
                  {savingManual ? "Saving…" : "Save profile"} <ArrowRight size={14} weight="bold" className="ml-2" />
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
